import { eventBus } from '@/shared/EventBus';
import { useGameStore } from './gameStore';

/**
 * Bridge: EventBus (źródło prawdy z Phaser'a) → Zustand store (czytany przez React).
 *
 * Wywołuj `installEventBusBridge()` RAZ przy starcie aplikacji, PRZED utworzeniem
 * Phaser.Game'a (żeby pierwsze emity z `WorldScene.create()` nie przeszły obok).
 *
 * HMR safety (Vite):
 *  - flaga `installed` zapobiega podwójnemu subscribe w tej samej instancji modułu
 *  - `import.meta.hot.dispose()` odpina listenery gdy moduł jest hot-replaced
 *    (zapobiega akumulacji starych listenerów na singletonie `eventBus`, który
 *    przeżywa HMR w innym pliku)
 *  - `import.meta.hot.accept()` przyjmuje self-update i re-instaluje bridge,
 *    żeby Vite nie musiał robić full page refresh
 *
 * W produkcji `import.meta.hot` jest `undefined` → cały blok pomijany.
 */
let installed = false;
let offCoins: (() => void) | null = null;
let offPower: (() => void) | null = null;
let offGoldRainbowBonus: (() => void) | null = null;

export function installEventBusBridge(): void {
  if (installed) return;
  installed = true;

  offCoins = eventBus.on('coins:changed', (e) => {
    useGameStore.getState().setCoins(e.newAmount);
  });

  offPower = eventBus.on('power:changed', (e) => {
    useGameStore.getState().setPower(e.newAmount);
  });

  offGoldRainbowBonus = eventBus.on('goldRainbowBonus:changed', (e) => {
    useGameStore.getState().setGoldRainbowCoinBonus(e.bonusPct);
  });
}

// ── Vite HMR lifecycle ──────────────────────────────────────────────────────
if (import.meta.hot) {
  // Przed podmianą modułu: odpinamy listenery żeby singleton eventBus nie
  // miał stale rosnącej kolejki subskrypcji z poprzednich edycji.
  import.meta.hot.dispose(() => {
    offCoins?.();
    offPower?.();
    offGoldRainbowBonus?.();
    offCoins = null;
    offPower = null;
    offGoldRainbowBonus = null;
    installed = false;
  });

  // Po podmianie modułu: nowa instancja modułu re-instaluje bridge automatycznie,
  // nie wymuszając full page refresh.
  import.meta.hot.accept((newModule) => {
    newModule?.installEventBusBridge();
  });
}
